import Service, { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import { registerDestructor } from "@ember/destroyable";
import type Owner from "@ember/owner";
import type AudioService from "#services/audio.ts";
import type { Song } from "#models/song.ts";
import type { Sound } from "#models/sound.ts";
import type { Channel } from "#models/channel.ts";

const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

export const MIN_BPM = 6;
export const MAX_BPM = 300;

/**
 * Service coordinating drum machine transport (play/pause/stop), clock ticking,
 * step evaluation across channels, and audio dispatch via AudioService.
 */
export default class SequencerService extends Service {
  @service declare audio: AudioService;

  @tracked isPlaying = false;
  @tracked currentTick: number | null = null;
  @tracked song: Song | null = null;

  private timerId: number | null = null;

  constructor(owner?: Owner) {
    super(owner);
    registerDestructor(this, () => {
      this.stop();
    });
  }

  /**
   * Interval in milliseconds for a 16th-note tick based on the song tempo.
   */
  get intervalMs(): number {
    const tempo = this.song?.tempo ?? 100;
    const safeTempo = clamp(tempo, MIN_BPM, MAX_BPM);
    return (60 / safeTempo / 4) * 1000;
  }

  /**
   * Sets the active song for playback.
   */
  loadSong = (song: Song): void => {
    this.song = song;
  };

  /**
   * Starts playback from the current tick position.
   */
  play = (): void => {
    if (this.isPlaying) {
      return;
    }

    // Warm up/resume audio context on user action
    this.audio.getContext();

    this.isPlaying = true;

    if (this.currentTick === null) {
      this.tick();
    } else {
      this.scheduleNextTick(this.intervalMs);
    }
  };

  /**
   * Pauses playback, preserving the current tick index.
   */
  pause = (): void => {
    this.isPlaying = false;
    this.clearTimer();
  };

  /**
   * Stops playback and resets the tick counter to null (idle/stopped).
   */
  stop = (): void => {
    this.pause();
    this.currentTick = null;
  };

  /**
   * Toggles between play and pause.
   */
  togglePlay = (): void => {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  };

  /**
   * Resets the tick counter to null (idle/stopped) without altering playing state.
   */
  reset = (): void => {
    this.currentTick = null;
  };

  /**
   * Executes a single sequencer tick: advances currentTick to the active step,
   * evaluates channel steps, plays active sounds, and schedules the next tick if playing.
   */
  tick = (): void => {
    this.clearTimer();

    const nextTick = this.currentTick === null ? 0 : this.currentTick + 1;
    this.currentTick = nextTick;
    this.evaluateAndPlayStep(nextTick);

    if (this.isPlaying) {
      this.scheduleNextTick(this.intervalMs);
    }
  };

  /**
   * Evaluates each channel's active step for the given tick and dispatches sound.
   */
  evaluateAndPlayStep = (tick: number): void => {
    if (!this.song?.channels) {
      return;
    }

    for (const channel of this.song.channels) {
      if (channel.isMuted || !channel.steps || channel.steps.length === 0) {
        continue;
      }

      const stepIndex = tick % channel.steps.length;
      const step = channel.steps[stepIndex];

      if (step && step.enabled && step.velocity > 0) {
        const volume = step.velocity * channel.volume;
        this.audio.playSound(channel.sound, volume);
      }
    }
  };

  /**
   * Schedules the next tick after the given delay in milliseconds.
   */
  private scheduleNextTick = (delay: number): void => {
    this.timerId = window.setTimeout(this.tick, delay);
  };

  /**
   * Clears any pending tick timer.
   */
  private clearTimer = (): void => {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  };

  /**
   * Auditions/previews a sound immediately with an optional volume.
   */
  previewSound = (sound: Sound, volume: number = 1.0): void => {
    this.audio.playSound(sound, volume);
  };

  /**
   * Auditions/previews a channel's sound at the channel's volume (unless muted).
   */
  previewChannel = (channel: Channel): void => {
    if (channel.isMuted) {
      return;
    }
    this.audio.playSound(channel.sound, channel.volume);
  };
}

declare module "@ember/service" {
  interface Registry {
    sequencer: SequencerService;
  }
}
